import { useCallback, useEffect, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { getApplicationToken } from '../hooks/useApplicationAuth'
import { useAuthenticateUser } from '../hooks/useAuthenticateUser'
import { useExecuteAuth } from '../hooks/useExecuteAuth'
import { useGetUserInfo } from '../hooks/useGetUserInfo'
import { useModalApplication } from '../hooks/useModalApplication'
import { AuthResult } from '../models/applicationConnection'
import { PasswordsStep } from './ConnectionSteps/PasswordsStep'
import { ResultStep } from './ConnectionSteps/ResultStep'

type FlowState = 'svsPassword' | 'appPassword' | 'result'

export function useAppAuthenticationManager() {
  const { t } = useTranslation('common')
  const { handleModal, updateModal, resetModal } = useModalApplication()
  const { handleExecuteAuth } = useExecuteAuth()
  const { handleAuthenticateUser } = useAuthenticateUser()
  const { userInfo } = useGetUserInfo()

  const [currentState, setCurrentState] = useState<FlowState>('svsPassword')
  const [authResult, setAuthResult] = useState<AuthResult>({
    success: false,
    loading: false,
  })
  const [authError, setAuthError] = useState<boolean>(false)
  const [friendlyMessage, setFriendlyMessage] = useState<string | null>(null)
  const [appData, setAppData] = useState<{
    applicationId: string
    applicationName: string
    userId: string
    accountUserId: string
    accountName?: string
  } | null>(null)
  const [svsPasswordValid, setSvsPasswordValid] = useState(false)
  const [appPasswordValid, setAppPasswordValid] = useState(false)
  const [errorSource, setErrorSource] = useState<FlowState | null>(null)
  const svsPasswordRef = useRef<string>('')
  const appPasswordRef = useRef<string>('')

  const renderStep = () => {
    if (!appData) return null

    const stepNumber =
      currentState === 'svsPassword'
        ? 1
        : currentState === 'appPassword'
          ? 2
          : 3

    switch (currentState) {
      case 'svsPassword':
        return (
          <PasswordsStep
            key="svsPassword"
            appData={appData}
            stepInfo={{ current: stepNumber, total: 3 }}
            onValidationChange={setSvsPasswordValid}
            hasActivationFlow={true}
            customDescription={t(
              'app_authentication.steps.service_solutions_description',
            )}
            onFormChange={(values) => {
              const formValues = values as { newPassword?: string }
              svsPasswordRef.current = formValues.newPassword || ''
            }}
          />
        )
      case 'appPassword':
        return (
          <PasswordsStep
            key="appPassword"
            appData={appData}
            stepInfo={{ current: stepNumber, total: 3 }}
            onValidationChange={setAppPasswordValid}
            hasActivationFlow={true}
            customDescription={t(
              'app_authentication.steps.application_description',
              {
                applicationName: appData.applicationName,
                accountName: appData.accountName
                  ? ` (${appData.accountName})`
                  : '',
              },
            )}
            customLabel={t(
              'app_connection.passwords.existing_user.application_label',
              {
                applicationName: appData.applicationName,
              },
            )}
            customPlaceholder={t(
              'app_connection.passwords.existing_user.application_placeholder',
              {
                applicationName: appData.applicationName,
              },
            )}
            customInfoMessage={t(
              'app_connection.passwords.existing_user.warning_message',
              {
                applicationName: appData.applicationName,
              },
            )}
            onFormChange={(values) => {
              const formValues = values as { newPassword?: string }
              appPasswordRef.current = formValues.newPassword || ''
            }}
          />
        )
      case 'result':
        return (
          <ResultStep
            appData={appData}
            authResult={authResult}
            stepInfo={{ current: stepNumber, total: 3 }}
            authError={authError}
            namespace="app_authentication"
            friendlyMessage={friendlyMessage}
          />
        )
      default:
        return null
    }
  }

  const handleNext = useCallback(async () => {
    if (!appData) return

    switch (currentState) {
      case 'svsPassword': {
        if (!svsPasswordValid || !svsPasswordRef.current) {
          return
        }

        try {
          await getApplicationToken({
            username: userInfo?.Person?.email || appData.userId,
            password: svsPasswordRef.current,
          })

          setSvsPasswordValid(false)
          setAppPasswordValid(false)
          setCurrentState('appPassword')
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setErrorSource('svsPassword')
          setCurrentState('result')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'appPassword': {
        if (!appPasswordValid || !appPasswordRef.current) {
          return
        }

        setAuthResult({ success: false, loading: true })
        setAuthError(false)
        setCurrentState('result')

        try {
          await handleAuthenticateUser({
            applicationAccountUsersId: appData.accountUserId,
            currentPassword: appPasswordRef.current,
            applicationPassword: svsPasswordRef.current,
          })

          const response = await handleExecuteAuth(appData.accountUserId)
          const loginUrl = `${response.application_url}?token=${response.token}`

          setAuthResult({ success: true, loading: false })
          queryClient.refetchQueries({ queryKey: ['/user/get-user-info'] })
          queryClient.invalidateQueries({
            queryKey: ['/user/current-accounts'],
          })

          setTimeout(() => {
            window.open(loginUrl, '_blank')
          }, 2000)
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setErrorSource('appPassword')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'result': {
        setCurrentState('svsPassword')
        setAuthResult({ success: false, loading: false })
        setAppData(null)
        setSvsPasswordValid(false)
        setAppPasswordValid(false)
        setFriendlyMessage(null)
        setErrorSource(null)
        setAuthError(false)
        svsPasswordRef.current = ''
        appPasswordRef.current = ''
        resetModal()
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appData,
    currentState,
    resetModal,
    svsPasswordValid,
    appPasswordValid,
    handleExecuteAuth,
    handleAuthenticateUser,
    authError,
    errorSource,
    userInfo?.Person?.email,
  ])

  const getCancelButtonText = useCallback(() => {
    if (currentState === 'result' && authError)
      return t('app_connection.buttons.back')
    if (currentState === 'svsPassword' || currentState === 'appPassword')
      return t('app_connection.buttons.back')
    return undefined
  }, [currentState, authError, t])

  const handleBack = useCallback(() => {
    if (currentState === 'svsPassword') {
      setCurrentState('svsPassword')
      setAuthResult({ success: false, loading: false })
      setAppData(null)
      setSvsPasswordValid(false)
      setAppPasswordValid(false)
      setFriendlyMessage(null)
      setErrorSource(null)
      setAuthError(false)
      svsPasswordRef.current = ''
      appPasswordRef.current = ''
      resetModal()
    } else if (currentState === 'appPassword') {
      setCurrentState('svsPassword')
      setAppPasswordValid(false)
    } else if (currentState === 'result' && authError) {
      if (errorSource === 'svsPassword') {
        setCurrentState('svsPassword')
      } else if (errorSource === 'appPassword') {
        setCurrentState('appPassword')
      }
      setAuthError(false)
      setAuthResult({ success: false, loading: false })
      setFriendlyMessage(null)
      setErrorSource(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState, authError, errorSource])

  useEffect(() => {
    if (!appData) return

    const getButtonText = () => {
      if (currentState === 'svsPassword')
        return t('app_authentication.buttons.authenticate')
      if (currentState === 'appPassword')
        return t('app_authentication.buttons.authenticate')
      if (currentState === 'result') {
        return authError
          ? t('app_authentication.buttons.close')
          : t('app_authentication.buttons.close')
      }
      return ''
    }

    const shouldShowButtons = (() => {
      if (currentState === 'svsPassword' || currentState === 'appPassword')
        return true
      if (currentState === 'result') return !authResult.loading
      return false
    })()

    const isDisabled = (() => {
      if (currentState === 'svsPassword') return !svsPasswordValid
      if (currentState === 'appPassword') return !appPasswordValid
      return false
    })()

    updateModal({
      title: t('app_authentication.modal.title'),
      description: renderStep(),
      confirmText: getButtonText(),
      cancelText: getCancelButtonText(),
      showButtons: shouldShowButtons,
      onConfirm: handleNext,
      onCancel: getCancelButtonText() ? handleBack : undefined,
      disabled: isDisabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentState,
    authResult,
    appData,
    svsPasswordValid,
    appPasswordValid,
    authError,
    friendlyMessage,
    errorSource,
    t,
    getCancelButtonText,
    handleBack,
  ])

  const handleAuthenticateApplication = async (
    applicationId: string,
    userId: string,
    applicationName: string,
    accountUserId: string,
    accountName?: string,
  ) => {
    setAppData({
      applicationId,
      applicationName,
      userId,
      accountUserId,
      accountName,
    })
    setCurrentState('svsPassword')
    setSvsPasswordValid(false)
    setAppPasswordValid(false)

    handleModal({
      title: t('app_authentication.modal.title'),
      description: renderStep(),
      showButtons: true,
      onConfirm: handleNext,
    })
  }

  return {
    handleAuthenticateApplication,
  }
}
